import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getTickets, getDashboardStats } from '../lib/api';
import { statusConfig, priorityConfig, formatRelativeDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    Ticket, 
    Search, 
    LogOut, 
    Clock, 
    CheckCircle, 
    AlertCircle,
    AlertTriangle,
    Filter,
    Users,
    Inbox,
    RefreshCw,
    Settings,
    Tag
} from 'lucide-react';

export default function OperatorDashboard() {
    const { user, logout, isAdmin } = useAuth();
    const navigate = useNavigate();
    const [tickets, setTickets] = useState([]);
    const [stats, setStats] = useState({
        totale: 0,
        aperti: 0,
        in_lavorazione: 0,
        in_attesa_risposta: 0,
        risolti: 0,
        chiusi: 0,
        urgenti: 0
    });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [ticketsRes, statsRes] = await Promise.all([
                getTickets(),
                getDashboardStats()
            ]);
            setTickets(ticketsRes.data);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredTickets = tickets.filter(ticket => {
        const matchesSearch = ticket.oggetto.toLowerCase().includes(searchTerm.toLowerCase()) ||
                              ticket.numero_ticket.toLowerCase().includes(searchTerm.toLowerCase()) ||
                              ticket.cliente_nome.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || ticket.stato === statusFilter;
        const matchesPriority = priorityFilter === 'all' || ticket.priorita === priorityFilter;
        return matchesSearch && matchesStatus && matchesPriority;
    });

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
                <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center">
                                <Ticket className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-lg font-bold text-slate-900">Dashboard Operatore</h1>
                                <p className="text-xs text-slate-500">
                                    {user?.nome} {user?.cognome} • {user?.ruolo === 'admin' ? 'Amministratore' : 'Operatore'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {isAdmin() && (
                                <>
                                    <Button 
                                        variant="ghost" 
                                        onClick={() => navigate('/staff')}
                                        data-testid="staff-management-btn"
                                    >
                                        <Users className="w-4 h-4 mr-2" />
                                        Staff
                                    </Button>
                                    <Button 
                                        variant="ghost" 
                                        onClick={() => navigate('/users')}
                                        data-testid="users-management-btn"
                                    >
                                        <Users className="w-4 h-4 mr-2" />
                                        Utenti
                                    </Button>
                                    <Button 
                                        variant="ghost" 
                                        onClick={() => navigate('/categories')}
                                        data-testid="categories-management-btn"
                                    >
                                        <Tag className="w-4 h-4 mr-2" />
                                        Categorie
                                    </Button>
                                    <Button 
                                        variant="ghost" 
                                        onClick={() => navigate('/admin/settings')}
                                        data-testid="admin-settings-btn"
                                    >
                                        <Settings className="w-4 h-4 mr-2" />
                                        Impostazioni
                                    </Button>
                                </>
                            )}
                            <Button 
                                variant="ghost" 
                                onClick={() => navigate('/settings')}
                                data-testid="user-settings-btn"
                            >
                                <Settings className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" onClick={loadData} data-testid="refresh-btn">
                                <RefreshCw className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" onClick={handleLogout} data-testid="logout-btn">
                                <LogOut className="w-4 h-4 mr-2" />
                                Esci
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
                    <Card className="border-slate-200">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-xs font-medium text-slate-500">Totale</CardTitle>
                            <Inbox className="w-4 h-4 text-slate-400" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-slate-900">{stats.totale}</div>
                        </CardContent>
                    </Card>
                    <Card className="border-slate-200 border-l-4 border-l-blue-500">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-xs font-medium text-slate-500">Aperti</CardTitle>
                            <AlertCircle className="w-4 h-4 text-blue-500" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-blue-600">{stats.aperti}</div>
                        </CardContent>
                    </Card>
                    <Card className="border-slate-200 border-l-4 border-l-purple-500">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-xs font-medium text-slate-500">In Lavorazione</CardTitle>
                            <Clock className="w-4 h-4 text-purple-500" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-purple-600">{stats.in_lavorazione}</div>
                        </CardContent>
                    </Card>
                    <Card className="border-slate-200 border-l-4 border-l-amber-500">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-xs font-medium text-slate-500">In Attesa</CardTitle>
                            <Users className="w-4 h-4 text-amber-500" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-amber-600">{stats.in_attesa_risposta}</div>
                        </CardContent>
                    </Card>
                    <Card className="border-slate-200 border-l-4 border-l-emerald-500">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-xs font-medium text-slate-500">Risolti</CardTitle>
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-emerald-600">{stats.risolti}</div>
                        </CardContent>
                    </Card>
                    <Card className="border-slate-200">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-xs font-medium text-slate-500">Chiusi</CardTitle>
                            <CheckCircle className="w-4 h-4 text-slate-400" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-slate-600">{stats.chiusi}</div>
                        </CardContent>
                    </Card>
                    <Card className="border-slate-200 border-l-4 border-l-red-500">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-xs font-medium text-slate-500">Urgenti</CardTitle>
                            <AlertTriangle className="w-4 h-4 text-red-500" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-red-600">{stats.urgenti}</div>
                        </CardContent>
                    </Card>
                </div>

                {/* Filters */}
                <Card className="border-slate-200 mb-6">
                    <CardContent className="pt-6">
                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input
                                    placeholder="Cerca per ticket, oggetto o cliente..."
                                    className="pl-10"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    data-testid="search-input"
                                />
                            </div>
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="w-[180px]" data-testid="status-filter">
                                    <Filter className="w-4 h-4 mr-2" />
                                    <SelectValue placeholder="Stato" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tutti gli stati</SelectItem>
                                    <SelectItem value="Aperto">Aperto</SelectItem>
                                    <SelectItem value="In attesa risposta">In attesa risposta</SelectItem>
                                    <SelectItem value="In lavorazione">In lavorazione</SelectItem>
                                    <SelectItem value="Risolto">Risolto</SelectItem>
                                    <SelectItem value="Chiuso">Chiuso</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                                <SelectTrigger className="w-[180px]" data-testid="priority-filter">
                                    <AlertTriangle className="w-4 h-4 mr-2" />
                                    <SelectValue placeholder="Priorità" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tutte le priorità</SelectItem>
                                    <SelectItem value="Bassa">Bassa</SelectItem>
                                    <SelectItem value="Media">Media</SelectItem>
                                    <SelectItem value="Alta">Alta</SelectItem>
                                    <SelectItem value="Urgente">Urgente</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Tickets Table */}
                <Card className="border-slate-200">
                    <CardContent className="p-0">
                        {loading ? (
                            <div className="text-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto"></div>
                                <p className="mt-4 text-slate-500">Caricamento...</p>
                            </div>
                        ) : filteredTickets.length === 0 ? (
                            <div className="text-center py-12">
                                <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-slate-900 mb-2">Nessun ticket trovato</h3>
                                <p className="text-slate-500">Prova a modificare i filtri di ricerca</p>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-[140px]">Ticket</TableHead>
                                        <TableHead>Oggetto</TableHead>
                                        <TableHead>Cliente</TableHead>
                                        <TableHead>Categoria</TableHead>
                                        <TableHead>Stato</TableHead>
                                        <TableHead>Priorità</TableHead>
                                        <TableHead>Assegnato</TableHead>
                                        <TableHead className="text-right">Aggiornato</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredTickets.map((ticket) => (
                                        <TableRow 
                                            key={ticket.id}
                                            className="cursor-pointer hover:bg-slate-50"
                                            onClick={() => navigate(`/tickets/${ticket.id}`)}
                                            data-testid={`ticket-row-${ticket.id}`}
                                        >
                                            <TableCell>
                                                <code className="text-xs font-mono text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                                                    {ticket.numero_ticket}
                                                </code>
                                            </TableCell>
                                            <TableCell>
                                                <div className="max-w-[300px]">
                                                    <p className="font-medium text-slate-900 truncate">{ticket.oggetto}</p>
                                                    <p className="text-xs text-slate-500 truncate">{ticket.descrizione}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div>
                                                    <p className="text-sm text-slate-900">{ticket.cliente_nome}</p>
                                                    <p className="text-xs text-slate-500">{ticket.cliente_email}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-sm text-slate-600">{ticket.categoria}</span>
                                            </TableCell>
                                            <TableCell>
                                                <Badge 
                                                    variant="outline"
                                                    className={`${statusConfig[ticket.stato]?.bg} ${statusConfig[ticket.stato]?.text} ${statusConfig[ticket.stato]?.border}`}
                                                >
                                                    {ticket.stato}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge 
                                                    variant="outline"
                                                    className={`${priorityConfig[ticket.priorita]?.bg} ${priorityConfig[ticket.priorita]?.text}`}
                                                >
                                                    {ticket.priorita}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-sm text-slate-600">
                                                    {ticket.assegnato_nome || '-'}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <span className="text-sm text-slate-500">
                                                    {formatRelativeDate(ticket.updated_at)}
                                                </span>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>
            </main>
        </div>
    );
}
